import React, { useEffect, useMemo, useState } from 'react';
import ModuleContext from 'CMFW/context/ModuleContext';
import Grid from 'CMFW/grid/Grid';
import { TranslationBankModel } from 'models/TranslationBankModel';
import { TranslationBankItemsModel } from 'models/TranslationBankItemsModel';
import { LoadingItem } from 'CMFW/layout/Loading';
import { EditorRow, EditorGapGreedy } from 'components/common/styles';
import { Button } from '@material-ui/core';
import axios from 'axios';
import DeleteButton from 'CMFW/button/DeleteButton';
import { IItemId } from 'types/content';
import { IFields } from 'CMFW/grid/fields';

const SYNC_TYPES = ['kline', 'tableliteral', 'panelintro'];

interface IProps {
  itemsModel: TranslationBankItemsModel;
  bankModel: TranslationBankModel;
  onSelectItem: (itemId: number) => void;
  onCopyClick: () => void;
  agentType?: string;
}

const BankItemsList: React.FC<IProps> = ({ itemsModel, bankModel, onSelectItem, onCopyClick, agentType }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const isSyncType = agentType ? SYNC_TYPES.includes(agentType) : false;

  useEffect(() => {
    setSyncResult(null);
  }, [itemsModel.bankId]);

  const cellRenderers = useMemo(
    () => ({
      [IFields.translationStatus]: 'translationDoneStatusRenderer',
    }),
    [],
  );

  const handleCreate = async () => {
    const result = await itemsModel.createNewItem();
    if (result.ok && result.data) {
      onSelectItem(result.data.id);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await itemsModel.syncBank();
      if (result) {
        setSyncResult(`Created: ${result.created}, Deleted: ${result.deleted}, Skipped: ${result.skipped}`);
        await itemsModel.getGrid();
        await bankModel.updateGridRow(bankModel.itemId, 'update');
      }
    } catch {
      setSyncResult('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleRowClick = (data: IItemId) => {
    onSelectItem(data.id);
  };

  return (
    <ModuleContext.Provider value={itemsModel}>
      <Grid handleRowClick={handleRowClick} cellRenderers={cellRenderers} />
      <EditorRow>
        <ModuleContext.Provider value={bankModel}>
          <DeleteButton />
        </ModuleContext.Provider>
        <Button
          style={{ marginLeft: 4 }}
          variant="outlined"
          size="small"
          onClick={async () => {
            const response = await axios.get<{ fileName: string; fileData: string }>(
              `/translation-banks/${bankModel.itemId}/export-csv`,
            );
            const blob = new Blob([response.data.fileData], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = response.data.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }}
        >
          CSV
        </Button>
        <EditorGapGreedy />
        {syncResult && <span style={{ fontSize: 12, color: '#666', marginRight: 8 }}>{syncResult}</span>}
        {isSyncType ? (
          <Button variant="outlined" size="small" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
        ) : (
          <>
            <Button variant="outlined" size="small" onClick={handleCreate} disabled={itemsModel.loadingItem}>
              {itemsModel.loadingItem ? 'Creating...' : 'Create'}
            </Button>
            <Button variant="outlined" size="small" onClick={onCopyClick} disabled={itemsModel.loadingItem}>
              Copy
            </Button>
          </>
        )}
      </EditorRow>
      <LoadingItem />
    </ModuleContext.Provider>
  );
};

export default BankItemsList;
